import { useLayoutEffect, useState } from "react";
import { useNodeId, useStore, useStoreApi, Position } from "@xyflow/react";
import { useShallow } from "zustand/react/shallow";

import {
  WORKSPACE_NODE_CARD_HEIGHT,
  WORKSPACE_NODE_CARD_WIDTH,
} from "./workspaceGraphConstants";

/** Approx screen width of the actions menu (toolbar does not scale with zoom). */
export const WORKSPACE_NODE_TOOLBAR_MENU_WIDTH_PX = 288;
/**
 * Typical menu height for a mid-size actions list. Prefer measuring the real
 * menu after paint when possible — this is only the pre-measure fallback.
 */
export const WORKSPACE_NODE_TOOLBAR_MENU_HEIGHT_PX = 220;
export const WORKSPACE_NODE_TOOLBAR_OFFSET_PX = 8;
const VIEWPORT_PAD_PX = 12;
/** Covering a clickable node is worse than a few pixels of pane overflow. */
const NODE_OVERLAP_WEIGHT = 12;

export type WorkspaceNodeToolbarAlign = "start" | "center" | "end";

export type WorkspaceNodeToolbarPlacement = {
  position: (typeof Position)[keyof typeof Position];
  align: WorkspaceNodeToolbarAlign;
};

/** Shrink the anchor rect within a node's Flow footprint (e.g. skip diamond overhang). */
export type WorkspaceNodeToolbarAnchorInsets = {
  left?: number;
  top?: number;
  right?: number;
  bottom?: number;
};

type Rect = { left: number; top: number; right: number; bottom: number };

type ScreenNodeRect = Rect & { id: string };

const DEFAULT_PLACEMENT: WorkspaceNodeToolbarPlacement = {
  position: Position.Left,
  align: "start",
};

function rectArea(rect: Rect): number {
  return Math.max(0, rect.right - rect.left) * Math.max(0, rect.bottom - rect.top);
}

function intersectionArea(a: Rect, b: Rect): number {
  const left = Math.max(a.left, b.left);
  const top = Math.max(a.top, b.top);
  const right = Math.min(a.right, b.right);
  const bottom = Math.min(a.bottom, b.bottom);
  return Math.max(0, right - left) * Math.max(0, bottom - top);
}

function overflowArea(menu: Rect, pane: Rect): number {
  const inside: Rect = {
    left: Math.max(menu.left, pane.left),
    top: Math.max(menu.top, pane.top),
    right: Math.min(menu.right, pane.right),
    bottom: Math.min(menu.bottom, pane.bottom),
  };
  return Math.max(0, rectArea(menu) - rectArea(inside));
}

function nodeOverlapArea(menu: Rect, others: ScreenNodeRect[]): number {
  let total = 0;
  for (const other of others) {
    total += intersectionArea(menu, other);
  }
  return total;
}

function menuRectForPosition(args: {
  position: WorkspaceNodeToolbarPlacement["position"];
  align: WorkspaceNodeToolbarAlign;
  node: Rect;
  menuWidth: number;
  menuHeight: number;
  offset: number;
}): Rect {
  const { position, align, node, menuWidth, menuHeight, offset } = args;
  const nodeWidth = node.right - node.left;
  const nodeHeight = node.bottom - node.top;

  const alignAlong = (start: number, size: number, span: number): number => {
    if (align === "center") return start + (span - size) / 2;
    if (align === "end") return start + span - size;
    return start;
  };

  if (position === Position.Left) {
    const top = alignAlong(node.top, menuHeight, nodeHeight);
    return {
      left: node.left - offset - menuWidth,
      top,
      right: node.left - offset,
      bottom: top + menuHeight,
    };
  }
  if (position === Position.Right) {
    const top = alignAlong(node.top, menuHeight, nodeHeight);
    return {
      left: node.right + offset,
      top,
      right: node.right + offset + menuWidth,
      bottom: top + menuHeight,
    };
  }
  if (position === Position.Top) {
    const left = alignAlong(node.left, menuWidth, nodeWidth);
    return {
      left,
      top: node.top - offset - menuHeight,
      right: left + menuWidth,
      bottom: node.top - offset,
    };
  }
  const left = alignAlong(node.left, menuWidth, nodeWidth);
  return {
    left,
    top: node.bottom + offset,
    right: left + menuWidth,
    bottom: node.bottom + offset + menuHeight,
  };
}

function scorePlacement(menu: Rect, pane: Rect, others: ScreenNodeRect[]): number {
  const overflow = overflowArea(menu, pane);
  const overlap = nodeOverlapArea(menu, others);
  // Soft tradeoff: keep the menu usable, but do not bury sibling nodes users need to click.
  return overflow + overlap * NODE_OVERLAP_WEIGHT;
}

/**
 * Prefer sides that stay on-screen and cover the fewest other nodes.
 * Tie-break: Left → Right → Top → Bottom (horizontal first; keeps pipeline lane clear).
 */
export function resolveWorkspaceNodeToolbarPlacement(args: {
  node: Rect;
  pane: Rect;
  otherNodes?: ScreenNodeRect[];
  menuWidthPx?: number;
  menuHeightPx?: number;
  offsetPx?: number;
}): WorkspaceNodeToolbarPlacement {
  const menuWidth = args.menuWidthPx ?? WORKSPACE_NODE_TOOLBAR_MENU_WIDTH_PX;
  const menuHeight = args.menuHeightPx ?? WORKSPACE_NODE_TOOLBAR_MENU_HEIGHT_PX;
  const offset = args.offsetPx ?? WORKSPACE_NODE_TOOLBAR_OFFSET_PX;
  const others = args.otherNodes ?? [];

  const positions: Array<WorkspaceNodeToolbarPlacement["position"]> = [
    Position.Left,
    Position.Right,
    Position.Top,
    Position.Bottom,
  ];
  const aligns: WorkspaceNodeToolbarAlign[] = ["start", "end", "center"];

  let best: WorkspaceNodeToolbarPlacement = DEFAULT_PLACEMENT;
  let bestScore = Number.POSITIVE_INFINITY;

  for (const position of positions) {
    for (const align of aligns) {
      const menu = menuRectForPosition({
        position,
        align,
        node: args.node,
        menuWidth,
        menuHeight,
        offset,
      });
      const score = scorePlacement(menu, args.pane, others);
      if (score < bestScore) {
        bestScore = score;
        best = { position, align };
      }
    }
  }

  return best;
}

/** @deprecated Prefer resolveWorkspaceNodeToolbarPlacement — kept for older call sites/tests. */
export function resolveWorkspaceNodeToolbarSide(args: {
  nodeLeftPx: number;
  nodeRightPx: number;
  paneWidthPx: number;
  menuWidthPx?: number;
  offsetPx?: number;
}): typeof Position.Left | typeof Position.Right {
  const menuWidth = args.menuWidthPx ?? WORKSPACE_NODE_TOOLBAR_MENU_WIDTH_PX;
  const offset = args.offsetPx ?? WORKSPACE_NODE_TOOLBAR_OFFSET_PX;
  const spaceLeft = args.nodeLeftPx - offset - VIEWPORT_PAD_PX;
  const spaceRight = args.paneWidthPx - args.nodeRightPx - offset - VIEWPORT_PAD_PX;

  const leftFits = spaceLeft >= menuWidth;
  const rightFits = spaceRight >= menuWidth;
  if (leftFits) return Position.Left;
  if (rightFits) return Position.Right;
  return spaceLeft >= spaceRight ? Position.Left : Position.Right;
}

function measureOverlayInsets(domNode: HTMLDivElement | null): {
  right: number;
  bottom: number;
  top: number;
} {
  if (!domNode) return { right: 0, bottom: 0, top: 0 };
  const viewport = domNode.closest("[data-workspace-canvas-viewport]");
  if (!(viewport instanceof HTMLElement)) return { right: 0, bottom: 0, top: 0 };

  const paneRect = domNode.getBoundingClientRect();
  let right = 0;
  let bottom = 0;
  let top = 0;

  const drawer = viewport.querySelector<HTMLElement>("[data-workspace-node-drawer]");
  if (drawer) {
    const drawerRect = drawer.getBoundingClientRect();
    if (drawerRect.width > 0 && drawerRect.left < paneRect.right) {
      right = Math.max(right, paneRect.right - drawerRect.left);
    }
  }

  const pineDock = viewport.querySelector<HTMLElement>("[data-workspace-pine-dock-shell]");
  if (pineDock) {
    const dockRect = pineDock.getBoundingClientRect();
    if (dockRect.height > 0 && dockRect.top < paneRect.bottom) {
      bottom = Math.max(bottom, paneRect.bottom - dockRect.top);
    }
  }

  const combineBar = viewport.querySelector<HTMLElement>(
    "[data-workspace-combine-selection-bar]"
  );
  if (combineBar) {
    const barRect = combineBar.getBoundingClientRect();
    if (barRect.height > 0 && barRect.top < paneRect.bottom) {
      bottom = Math.max(bottom, paneRect.bottom - barRect.top);
    }
  }

  const toolbar = viewport.querySelector<HTMLElement>("[data-workspace-toolbar]");
  if (toolbar) {
    const toolbarRect = toolbar.getBoundingClientRect();
    if (toolbarRect.height > 0 && toolbarRect.bottom > paneRect.top) {
      top = Math.max(top, toolbarRect.bottom - paneRect.top);
    }
  }

  return { right, bottom, top };
}

function toScreenRect(
  absX: number,
  absY: number,
  width: number,
  height: number,
  tx: number,
  ty: number,
  zoom: number
): Rect {
  return {
    left: absX * zoom + tx,
    top: absY * zoom + ty,
    right: (absX + width) * zoom + tx,
    bottom: (absY + height) * zoom + ty,
  };
}

function placementEqual(
  a: WorkspaceNodeToolbarPlacement,
  b: WorkspaceNodeToolbarPlacement
): boolean {
  return a.position === b.position && a.align === b.align;
}

function resolveNodeScreenSize(entry: {
  measured?: { width?: number; height?: number };
  width?: number;
  height?: number;
}): { width: number; height: number } {
  const width = entry.measured?.width ?? entry.width ?? WORKSPACE_NODE_CARD_WIDTH;
  const height = entry.measured?.height ?? entry.height ?? WORKSPACE_NODE_CARD_HEIGHT;
  return {
    width: width > 0 ? width : WORKSPACE_NODE_CARD_WIDTH,
    height: height > 0 ? height : WORKSPACE_NODE_CARD_HEIGHT,
  };
}

function applyAnchorInsets(
  abs: { x: number; y: number },
  size: { width: number; height: number },
  anchorInsets?: WorkspaceNodeToolbarAnchorInsets
): { x: number; y: number; width: number; height: number } {
  const left = anchorInsets?.left ?? 0;
  const top = anchorInsets?.top ?? 0;
  const right = anchorInsets?.right ?? 0;
  const bottom = anchorInsets?.bottom ?? 0;
  return {
    x: abs.x + left,
    y: abs.y + top,
    width: Math.max(0, size.width - left - right),
    height: Math.max(0, size.height - top - bottom),
  };
}

function anchorInsetsKey(anchorInsets?: WorkspaceNodeToolbarAnchorInsets): string {
  if (!anchorInsets) return "";
  return `${anchorInsets.left ?? 0},${anchorInsets.top ?? 0},${anchorInsets.right ?? 0},${anchorInsets.bottom ?? 0}`;
}

function measureRenderedMenuSize(
  fallbackWidth: number,
  fallbackHeight: number
): { width: number; height: number } {
  if (typeof document === "undefined") {
    return { width: fallbackWidth, height: fallbackHeight };
  }
  const menu = document.querySelector<HTMLElement>("[data-workspace-node-actions]");
  if (!menu) return { width: fallbackWidth, height: fallbackHeight };
  const rect = menu.getBoundingClientRect();
  return {
    width: rect.width > 0 ? Math.ceil(rect.width) : fallbackWidth,
    height: rect.height > 0 ? Math.ceil(rect.height) : fallbackHeight,
  };
}

/**
 * Pick Left / Right / Top / Bottom (+ align) so the menu stays in the flow pane
 * and covers as few sibling nodes as possible.
 */
export function useWorkspaceNodeToolbarSide(
  isVisible: boolean,
  menuWidthPx: number = WORKSPACE_NODE_TOOLBAR_MENU_WIDTH_PX,
  menuHeightPx: number = WORKSPACE_NODE_TOOLBAR_MENU_HEIGHT_PX,
  anchorInsets?: WorkspaceNodeToolbarAnchorInsets
): WorkspaceNodeToolbarPlacement {
  const nodeId = useNodeId();
  const storeApi = useStoreApi();
  const anchorKey = anchorInsetsKey(anchorInsets);

  // Primitives only — object/array fields would defeat useShallow and loop forever.
  const layoutKey = useStore(
    useShallow((state) => {
      if (!isVisible || !nodeId) {
        return {
          nodeKey: "",
          otherNodesKey: "",
          paneWidthPx: 0,
          paneHeightPx: 0,
        };
      }

      const [tx, ty, zoom] = state.transform;
      const node = state.nodeLookup.get(nodeId);
      if (!node) {
        return {
          nodeKey: "",
          otherNodesKey: "",
          paneWidthPx: state.width,
          paneHeightPx: state.height,
        };
      }

      const abs = node.internals.positionAbsolute;
      const size = resolveNodeScreenSize(node);
      const anchor = applyAnchorInsets(abs, size, anchorInsets);
      const nodeRect = toScreenRect(
        anchor.x,
        anchor.y,
        anchor.width,
        anchor.height,
        tx,
        ty,
        zoom
      );
      const nodeKey = `${Math.round(nodeRect.left)},${Math.round(nodeRect.top)},${Math.round(nodeRect.right)},${Math.round(nodeRect.bottom)}`;

      const otherParts: string[] = [];
      for (const [id, entry] of Array.from(state.nodeLookup.entries())) {
        if (id === nodeId || entry.hidden) continue;
        const otherAbs = entry.internals.positionAbsolute;
        const otherSize = resolveNodeScreenSize(entry);
        const rect = toScreenRect(
          otherAbs.x,
          otherAbs.y,
          otherSize.width,
          otherSize.height,
          tx,
          ty,
          zoom
        );
        otherParts.push(
          `${id}:${Math.round(rect.left)},${Math.round(rect.top)},${Math.round(rect.right)},${Math.round(rect.bottom)}`
        );
      }

      return {
        nodeKey,
        otherNodesKey: otherParts.join("|"),
        paneWidthPx: state.width,
        paneHeightPx: state.height,
      };
    })
  );

  const [placement, setPlacement] = useState<WorkspaceNodeToolbarPlacement>(DEFAULT_PLACEMENT);

  useLayoutEffect(() => {
    if (!isVisible || !nodeId || !layoutKey.nodeKey) {
      setPlacement((prev) =>
        placementEqual(prev, DEFAULT_PLACEMENT) ? prev : DEFAULT_PLACEMENT
      );
      return;
    }

    const measure = (useRenderedMenuSize: boolean) => {
      const state = storeApi.getState();
      const [tx, ty, zoom] = state.transform;
      const node = state.nodeLookup.get(nodeId);
      if (!node) {
        setPlacement((prev) =>
          placementEqual(prev, DEFAULT_PLACEMENT) ? prev : DEFAULT_PLACEMENT
        );
        return;
      }

      const abs = node.internals.positionAbsolute;
      const size = resolveNodeScreenSize(node);
      const anchor = applyAnchorInsets(abs, size, anchorInsets);
      const nodeRect = toScreenRect(
        anchor.x,
        anchor.y,
        anchor.width,
        anchor.height,
        tx,
        ty,
        zoom
      );

      const otherNodes: ScreenNodeRect[] = [];
      for (const [id, entry] of Array.from(state.nodeLookup.entries())) {
        if (id === nodeId || entry.hidden) continue;
        const otherAbs = entry.internals.positionAbsolute;
        const otherSize = resolveNodeScreenSize(entry);
        otherNodes.push({
          id,
          ...toScreenRect(
            otherAbs.x,
            otherAbs.y,
            otherSize.width,
            otherSize.height,
            tx,
            ty,
            zoom
          ),
        });
      }

      const insets = measureOverlayInsets(state.domNode);
      const pane: Rect = {
        left: VIEWPORT_PAD_PX,
        top: VIEWPORT_PAD_PX + insets.top,
        right: Math.max(
          VIEWPORT_PAD_PX,
          layoutKey.paneWidthPx - VIEWPORT_PAD_PX - insets.right
        ),
        bottom: Math.max(
          VIEWPORT_PAD_PX,
          layoutKey.paneHeightPx - VIEWPORT_PAD_PX - insets.bottom
        ),
      };

      const rendered = useRenderedMenuSize
        ? measureRenderedMenuSize(menuWidthPx, menuHeightPx)
        : { width: menuWidthPx, height: menuHeightPx };

      const next = resolveWorkspaceNodeToolbarPlacement({
        node: nodeRect,
        pane,
        otherNodes,
        menuWidthPx: rendered.width,
        menuHeightPx: rendered.height,
      });
      setPlacement((prev) => (placementEqual(prev, next) ? prev : next));
    };

    measure(false);
    let raf2 = 0;
    // Remeasure after paint with the real menu size, and again once drawers/docks settle.
    const raf1 = window.requestAnimationFrame(() => {
      measure(true);
      raf2 = window.requestAnimationFrame(() => measure(true));
    });
    return () => {
      window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
    };
  }, [
    anchorInsets,
    anchorKey,
    isVisible,
    layoutKey.nodeKey,
    layoutKey.otherNodesKey,
    layoutKey.paneHeightPx,
    layoutKey.paneWidthPx,
    menuHeightPx,
    menuWidthPx,
    nodeId,
    storeApi,
  ]);

  return placement;
}
